/**
 * Раскрытая вилка: раскладка банка, лестница объёма, пометки, вход на площадку.
 *
 * Раскладка считается на клиенте через `lib/calc` — зеркало backend/forq/money.py.
 * Сверять его с бэкендом обязаны тесты: пользователь копирует именно эти суммы, и
 * расхождение во втором знаке читается как «сканер врёт».
 *
 * Лестница объёма приходит с сервера уже посчитанной по стакану: ROI при 100, 250 и
 * 500 — разный, потому что вторая ставка съедает лучшие уровни. Показывать одно
 * число и молчать про глубину — главная ложь сканеров: медиана исполнимого на
 * киберспорте Polymarket по лучшей цене — 13 долларов, а не 500.
 */

import { useState } from 'react';

import { oddsText, pct, roundAndRebalance } from '../lib/calc';
import { type Fork, entryHint, entryUrl, sideLabel } from '../lib/protocol';
import { bankVerdict } from '../lib/volume';
import { useFeed } from '../store/feed';

interface Props {
  fork: Fork;
  bank: number;
}

const STEP = '10';

/** «правило» / «правила» / «правил» — иначе строка читается как машинный вывод. */
const plural = (count: number): string => {
  const tail = count % 100;
  if (tail >= 11 && tail <= 14) return 'правил';
  switch (count % 10) {
    case 1:
      return 'правило';
    case 2:
    case 3:
    case 4:
      return 'правила';
    default:
      return 'правил';
  }
};

export function ForkCard({ fork, bank }: Props) {
  const [copied, setCopied] = useState<string | null>(null);
  // Легенда лежит в сторе целым объектом: селектор, собирающий новый объект,
  // уводит zustand 5 в бесконечный рендер (см. комментарий в store/feed.ts).
  const catalog = useFeed((s) => s.ruleCatalog);
  const odds = fork.o ?? [];
  const legs = fork.legs ?? [];
  const rules = fork.rl ?? [];
  const checked = Object.keys(catalog).length;
  const verdict = bankVerdict(fork, bank);
  const plan = odds.length >= 2 ? roundAndRebalance(String(bank), odds, STEP) : null;

  const copy = (value: string) => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(value);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <div className="card">
      <div className="grid">
        <div>
          <h3>раскладка банка {bank} $, шаг ставки {STEP}</h3>
          {plan === null ? (
            <p className="num">кэфы не пришли</p>
          ) : (
            <table className="plan">
              <thead>
                <tr>
                  <th>площадка</th>
                  <th>исход</th>
                  <th>кэф</th>
                  <th>ставка</th>
                  <th>выплата</th>
                </tr>
              </thead>
              <tbody>
                {odds.map((odd, i) => {
                  const leg = legs[i];
                  const stake = plan.stakes[i]?.toFixed(2) ?? '—';
                  return (
                    <tr key={`${fork.id}:${i}`}>
                      <td>
                        {entryUrl(leg) ? (
                          <a
                            href={entryUrl(leg) as string}
                            target="_blank"
                            rel="noreferrer noopener"
                            title={entryHint(leg)}
                            className={leg?.direct === false ? 'indirect' : undefined}
                          >
                            {leg?.s}
                            {leg?.direct === false && '↗'}
                          </a>
                        ) : (
                          (leg?.s ?? '—')
                        )}
                      </td>
                      <td>{leg ? sideLabel(leg.side) : '—'}</td>
                      <td>{oddsText(odd)}</td>
                      <td>
                        <button
                          type="button"
                          className="stake"
                          onClick={() => copy(stake)}
                          title="скопировать сумму"
                        >
                          {stake}
                        </button>
                      </td>
                      <td>{plan.payouts[i]?.toFixed(2) ?? '—'}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={3}>итого / ROI после округления</td>
                  <td>{plan.total.toFixed(2)}</td>
                  <td>{pct(plan.roi)}%</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h3>ROI по объёму — проход по стакану</h3>
          {/* Брутто живёт здесь, а не в строке ленты: это число для сверки со
              своим калькулятором, а решение принимают по ROI на свой банк. */}
          <p className="num" style={{ margin: '0 0 6px', color: 'var(--dim)', fontSize: 12 }}>
            в ленте {fork.roi ?? '—'}% · брутто {fork.roiGross ?? '—'}%
          </p>
          <table className="plan">
            <tbody>
              {(fork.vol ?? []).map((step) => (
                // Ступень, по которой посчитана строка ленты, помечена: иначе
                // непонятно, откуда взялось число в свёрнутом виде.
                <tr key={step.bank} className={step.bank === verdict.step ? 'used' : undefined}>
                  <td>банк {step.bank}</td>
                  <td>{step.roi}%</td>
                </tr>
              ))}
              <tr>
                <td>максимум в плюс</td>
                <td>{fork.maxVol ?? '—'}</td>
              </tr>
            </tbody>
          </table>
          {verdict.over && (
            <p style={{ color: 'var(--bad)', fontSize: 12 }}>
              банк {bank}$ в этот стакан не влезает — с него вилка уходит в минус
            </p>
          )}
          {(fork.limits ?? []).length > 0 && (
            <p className="num" style={{ color: 'var(--dim)', fontSize: 12 }}>
              ограничения: {(fork.limits ?? []).join('; ')}
            </p>
          )}
        </div>

        <div>
          <h3>что с этой вилкой не так</h3>
          {rules.length === 0 ? (
            <p style={{ color: 'var(--good)', fontSize: 13 }}>
              {/* Число правил берётся из легенды сервера, а не стоит в коде:
                  захардкоженное «проверено 8» становится ложью в день, когда на
                  бэкенде появляется девятое. */}
              правила анти-скама молчат
              {checked > 0 && ` — проверено ${checked} ${plural(checked)}`}
            </p>
          ) : (
            <ul className="rules">
              {rules.map((rule) => (
                <li key={rule.c}>
                  {rule.t}
                  {/* Под конкретным текстом — что это правило значит вообще.
                      Легенда приходит с сервера: правило добавляют на бэкенде, и
                      объяснить его фронт обязан без своей выкатки. */}
                  {catalog[rule.c] && (
                    <em style={{ display: 'block', color: 'var(--dim)', fontStyle: 'normal' }}>
                      {catalog[rule.c]}
                    </em>
                  )}
                </li>
              ))}
            </ul>
          )}
          <h3 style={{ marginTop: 12 }}>состав</h3>
          <p className="num" style={{ fontSize: 13 }}>
            {(legs[0]?.participants ?? []).join(' — ') || '—'}
          </p>
        </div>
      </div>
      {copied !== null && <div className="toast">скопировано: {copied}</div>}
    </div>
  );
}
